import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { Modal } from '../ui/Modal'
import { LoadingSpinner } from '../ui/LoadingSpinner'

export function SettingsAccountSection() {
  const { user, loading } = useAuth()
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const handleOpenPasswordModal = () => {
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setPasswordSuccess(false)
    setIsPasswordModalOpen(true)
  }

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false)
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setPasswordSuccess(false)
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess(false)

    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden')
      return
    }

    setIsUpdating(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setPasswordSuccess(true)
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => handleClosePasswordModal(), 1500)
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Error al cambiar la contraseña')
    } finally {
      setIsUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Cuenta</h3>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Cuenta</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="account-email" className="label">
              Correo electrónico
            </label>
            <input
              id="account-email"
              type="email"
              value={user?.email ?? ''}
              readOnly
              className="input bg-slate-50 cursor-not-allowed"
              aria-readonly
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleOpenPasswordModal}
              className="btn btn-secondary"
            >
              Cambiar contraseña
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isPasswordModalOpen}
        onClose={handleClosePasswordModal}
        title="Cambiar contraseña"
        size="sm"
      >
        <form onSubmit={handleChangePassword} className="space-y-4">
          {passwordError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
              Contraseña actualizada correctamente
            </div>
          )}
          <div>
            <label htmlFor="new-password" className="label">
              Nueva contraseña
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input"
              placeholder="Mínimo 6 caracteres"
              minLength={6}
              disabled={isUpdating || passwordSuccess}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="label">
              Confirmar contraseña
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder="Repite la contraseña"
              disabled={isUpdating || passwordSuccess}
              autoComplete="new-password"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={handleClosePasswordModal}
              className="btn btn-secondary"
              disabled={isUpdating}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isUpdating || passwordSuccess}
              className="btn btn-primary"
            >
              {isUpdating ? 'Actualizando...' : 'Cambiar contraseña'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  )
}
